import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import useAuth from '../../context/auth.context.jsx';
import { NAV_ITEMS } from './navbar.jsx';
import { GroupPill, GroupSwitcherSheet } from './appBar.jsx';
import LogoutConfirmSheet from './common/logoutConfirmSheet.jsx';
import PlanChip from './common/planChip.jsx';
import usePlanAccent from '../hooks/usePlanAccent.js';
import logo from '../assets/logo.png';

function isActive(item, pathname) {
    return (item.match || [item.path]).includes(pathname);
}

/**
 * Desktop chrome. Replaces both the app bar and the bottom nav above 1024px —
 * see ProtectedShell in App.jsx. Sits in the first grid column, which lands on
 * the right in RTL locales without any JS.
 */
export default function Sidebar() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { t } = useTranslation('common');
    const { userData } = useAuth();
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const planAccent = usePlanAccent();

    return (
        <aside className="sidebar" aria-label={t('mainNav')}>
            <div className="sidebar-brand">
                <span className="brand-logo-wrap">
                    <img src={logo} alt="" width="32" height="32" style={{ borderRadius: 8 }} />
                    {planAccent && <span className={`brand-logo-dot ${planAccent}`} aria-hidden="true" />}
                </span>
                <span className="fw-bold">Master Splitter</span>
            </div>

            <div className="sidebar-group">
                <GroupPill onClick={() => setSwitcherOpen(true)} />
            </div>

            <nav className="sidebar-nav">
                <div className="d-flex flex-column w-100" data-tour="main-nav" style={{ gap: 2 }}>
                {NAV_ITEMS.map(item => {
                    const active = isActive(item, pathname);
                    return (
                        <button
                            key={item.path}
                            type="button"
                            className={`sidebar-link ${active ? 'active' : ''}`}
                            onClick={() => navigate(item.path)}
                            aria-current={active ? 'page' : undefined}
                        >
                            <i className={`bi ${active ? item.iconActive : item.icon}`} aria-hidden="true" />
                            <span>{t(item.labelKey)}</span>
                        </button>
                    );
                })}
                </div>
                <button
                    type="button"
                    className={`sidebar-link ${pathname.startsWith('/games') ? 'active' : ''}`}
                    data-tour="games-entry"
                    onClick={() => navigate('/games')}
                >
                    <i className="bi bi-puzzle-fill" aria-hidden="true" />
                    <span>{t('navGames')}</span>
                </button>
            </nav>

            <div className="sidebar-foot">
                <div className="sidebar-user-row">
                    <span className="sidebar-user text-truncate">{userData?.user?.name}</span>
                    <PlanChip accent={planAccent} />
                </div>
                <button type="button" className="sidebar-link" onClick={() => setLogoutOpen(true)}>
                    <i className="bi bi-box-arrow-right" aria-hidden="true" />
                    <span>{t('logout')}</span>
                </button>
            </div>

            <GroupSwitcherSheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
            <LogoutConfirmSheet open={logoutOpen} onClose={() => setLogoutOpen(false)} />
        </aside>
    );
}
